/**
 * Registry-derived React reconciler for JSX → JSON dashboard serialization.
 *
 * This reconciler converts JSX dashboard trees into internal node structures
 * that can be serialized to DashboardGridData JSON. It auto-derives node
 * creators from the static dashboard registry, so adding new components
 * to the registry automatically enables them in JSX without reconciler changes.
 *
 * @example
 * ```tsx
 * const json = renderDashboard(
 *   <dashboard-grid rows={3} columns={12} title="My Dashboard">
 *     <grid-item row={1} column={1} columnSpan={6}>
 *       <dashboard-kpi value={42} label="Metric" />
 *     </grid-item>
 *   </dashboard-grid>
 * );
 * ```
 *
 * @module reconciler/reconciler
 */
import type { ReactNode } from 'react';
import ReactReconciler from 'react-reconciler';

import { dashboardRegistry } from '../registry.ts';
import type {
  ComponentNode,
  ComponentNodeKind,
  FragmentNode,
  GridItemNode,
  GridNode,
  Node,
  TextNode,
} from './types.ts';

type ElementType = string | ((props: Record<string, unknown>) => ReactNode);
type Props = Record<string, unknown>;

/**
 * Builds node creator functions from the dashboard registry.
 * This is called once at module load time, auto-registering all component types.
 *
 * Fixed elements:
 * - 'dashboard-grid' -> GridNode
 * - 'grid-item' -> GridItemNode
 *
 * Registry components:
 * - 'dashboard-{type.toLowerCase()}' -> ComponentNode with that type
 */
function buildNodeCreators(): Record<string, (props: Props) => Node> {
  const creators: Record<string, (props: Props) => Node> = {
    // Fixed structural elements
    'dashboard-grid': (props) => ({
      kind: 'grid',
      layout: {
        rows: typeof props.rows === 'number' ? props.rows : 3,
        columns: typeof props.columns === 'number' ? props.columns : 12,
      },
      title: typeof props.title === 'string' ? props.title : undefined,
      children: [],
    }),
    'grid-item': (props) => ({
      kind: 'grid-item',
      position: {
        row: typeof props.row === 'number' ? props.row : 0,
        column: typeof props.column === 'number' ? props.column : 0,
        rowSpan: typeof props.rowSpan === 'number' ? props.rowSpan : 1,
        columnSpan: typeof props.columnSpan === 'number' ? props.columnSpan : 1,
      },
      title: typeof props.title === 'string' ? props.title : undefined,
      child: null,
    }),
  };

  // Auto-generate component creators from registry
  for (const entry of dashboardRegistry) {
    // Map component type to JSX element name using kebab-case:
    // - 'chart' -> 'dashboard-chart'
    // - 'kpi' -> 'dashboard-kpi'
    // - 'timeseries-line' -> 'dashboard-timeseries-line'
    const kebabType = entry.type.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    const elementName = kebabType.startsWith('dashboard-') ? kebabType : `dashboard-${kebabType}`;
    creators[elementName] = (props): ComponentNode => ({
      kind: entry.type as ComponentNodeKind,
      props: { ...props, type: entry.type },
    });
  }

  return creators;
}

// Build creators at module load time
const nodeCreators = buildNodeCreators();

/**
 * Creates a node from an element type and props.
 * Falls back to fragment node for unknown types.
 */
const createNode = (type: ElementType, props: Props): Node => {
  if (typeof type === 'string') {
    const creator = nodeCreators[type];
    if (creator) {
      return creator(props);
    }
  }
  return { kind: 'fragment', children: [] };
};

/**
 * Removes children prop from props object (handled by reconciler).
 */
const omitChildren = (props: Props): Props => {
  const { children: _, ...rest } = props;
  return rest;
};

/**
 * Parent node type - nodes that can contain children.
 */
type ParentNode = GridNode | GridItemNode | FragmentNode;

/**
 * Appends a child node to a parent node.
 * Handles both array children (grid/fragment) and single child (grid-item).
 */
const appendNodeChild = (parent: unknown, child: unknown): void => {
  if (typeof parent === 'object' && parent !== null && 'children' in parent && Array.isArray(parent.children)) {
    parent.children.push(child as Node);
  } else if (
    typeof parent === 'object' &&
    parent !== null &&
    'child' in parent &&
    'kind' in parent &&
    parent.kind === 'grid-item'
  ) {
    const childNode = child as Node;
    if (childNode.kind !== 'text' && childNode.kind !== 'fragment') {
      (parent as GridItemNode).child = childNode as ComponentNode;
    }
  }
};

/**
 * Inserts a child node before another child in a parent node.
 * Only applies to nodes with array children.
 */
const insertNodeBefore = (parent: unknown, child: unknown, beforeChild: unknown): void => {
  if (typeof parent !== 'object' || parent === null || !('children' in parent) || !Array.isArray(parent.children)) {
    return;
  }
  const idx = parent.children.indexOf(beforeChild as Node);
  if (idx >= 0) {
    parent.children.splice(idx, 0, child as Node);
  } else {
    parent.children.push(child as Node);
  }
};

/**
 * Removes a child node from a parent node.
 * Only applies to nodes with array children.
 */
const removeNodeChild = (parent: unknown, child: unknown): void => {
  if (typeof parent !== 'object' || parent === null || !('children' in parent) || !Array.isArray(parent.children)) {
    return;
  }
  const idx = parent.children.indexOf(child as Node);
  if (idx >= 0) {
    parent.children.splice(idx, 1);
  }
};

/**
 * Host config for react-reconciler.
 * Implements minimal interface for synchronous tree building.
 */
const hostConfig: ReactReconciler.HostConfig<
  ElementType, // Type
  Props, // Props
  ParentNode, // Container
  Node, // Instance
  TextNode, // TextInstance
  Node, // SuspenseInstance
  Node, // HydratableInstance
  Node, // FormInstance
  Node, // PublicInstance
  object, // HostContext
  Node[], // ChildSet
  ReturnType<typeof setTimeout>, // TimeoutHandle
  -1, // NoTimeout
  null // TransitionStatus
> = {
  supportsMutation: true,
  supportsPersistence: false,
  supportsHydration: false,
  isPrimaryRenderer: false,

  // Timing
  scheduleTimeout: setTimeout,
  cancelTimeout: clearTimeout,
  noTimeout: -1,
  supportsMicrotasks: true,
  scheduleMicrotask: globalThis.queueMicrotask ?? ((fn) => Promise.resolve().then(fn)),

  // Context
  getRootHostContext: () => ({}),
  getChildHostContext: (parentHostContext) => parentHostContext,
  getPublicInstance: (instance) => instance,

  // Instance creation
  createInstance: (type, props) => createNode(type, omitChildren(props)),
  createTextInstance: (text): TextNode => ({ kind: 'text', value: text }),

  // Tree building - handles both array children (grid/fragment) and single child (grid-item)
  // SAFETY: react-reconciler types are intentionally permissive (Instance, TextInstance, etc.).
  // We control all node creation via createNode() which guarantees Node structure.
  appendInitialChild: (parentInstance, child) => appendNodeChild(parentInstance, child),
  appendChild: (parentInstance, child) => appendNodeChild(parentInstance, child),
  appendChildToContainer: (container, child) => {
    if ('children' in container && Array.isArray(container.children)) {
      container.children.push(child as Node);
    }
  },

  // Insertion - assertions safe per SAFETY note above
  insertBefore: (parentInstance, child, beforeChild) => insertNodeBefore(parentInstance, child, beforeChild),
  insertInContainerBefore: (container, child, beforeChild) => insertNodeBefore(container, child, beforeChild),

  // Removal - assertions safe per SAFETY note above
  removeChild: (parentInstance, child) => removeNodeChild(parentInstance, child),
  removeChildFromContainer: (container, child) => removeNodeChild(container, child),

  // Finalization
  finalizeInitialChildren: () => false,
  prepareForCommit: () => null,
  resetAfterCommit: () => {
    /* noop */
  },
  preparePortalMount: () => {
    /* noop */
  },

  // Text content
  shouldSetTextContent: () => false,
  resetTextContent: () => {
    /* noop */
  },

  // Updates
  commitUpdate: () => {
    /* noop */
  },
  commitTextUpdate: (textInstance, _oldText, newText) => {
    textInstance.value = newText;
  },

  // Container
  clearContainer: (container) => {
    if ('children' in container) {
      container.children = [];
    }
  },

  // Instance management
  getInstanceFromNode: () => null,
  beforeActiveInstanceBlur: () => {
    /* noop */
  },
  afterActiveInstanceBlur: () => {
    /* noop */
  },
  prepareScopeUpdate: () => {
    /* noop */
  },
  getInstanceFromScope: () => null,
  detachDeletedInstance: () => {
    /* noop */
  },

  // Priority
  setCurrentUpdatePriority: () => {
    /* noop */
  },
  getCurrentUpdatePriority: () => 16,
  resolveUpdatePriority: () => 16,

  // Form
  resetFormInstance: () => {
    /* noop */
  },

  // Callbacks
  requestPostPaintCallback: () => {
    /* noop */
  },

  // Transition
  shouldAttemptEagerTransition: () => false,
  trackSchedulerEvent: () => {
    /* noop */
  },
  resolveEventType: () => null,
  resolveEventTimeStamp: () => -1.1,

  // Suspense
  maySuspendCommit: () => false,
  preloadInstance: () => true,
  startSuspendingCommit: () => {
    /* noop */
  },
  suspendInstance: () => {
    /* noop */
  },
  waitForCommitToBeReady: () => null,

  // Transition context
  NotPendingTransition: null,
  // Required by react-reconciler interface but unused in synchronous rendering
  HostTransitionContext: {
    // biome-ignore lint/style/useNamingConvention: Required by react-reconciler
    $$typeof: Symbol.for('react.context'),
    Consumer: null as unknown as ReactReconciler.ReactContext<null>,
    Provider: null as unknown as ReactReconciler.ReactProviderType<null>,
    _currentValue: null,
    _currentValue2: null,
    _threadCount: 0,
  },
};

// Create reconciler instance
const reconcilerInstance = ReactReconciler(hostConfig);

/**
 * Renders JSX to an internal node tree.
 * Used by serialize.ts to build the tree before converting to JSON.
 */
export function renderToTree(element: ReactNode): Node {
  const root: FragmentNode = { kind: 'fragment', children: [] };
  const container = reconcilerInstance.createContainer(
    root,
    0, // LegacyRoot tag for sync rendering
    null, // hydrationCallbacks
    false, // isStrictMode
    null, // concurrentUpdatesByDefaultOverride
    '', // identifierPrefix
    () => {
      /* noop */
    }, // onUncaughtError
    () => {
      /* noop */
    }, // onCaughtError
    () => {
      /* noop */
    }, // onRecoverableError
    () => {
      /* noop */
    }, // onDefaultTransitionIndicator
  );

  // react-reconciler v0.33.0: Use updateContainerSync + flushSyncWork for synchronous rendering.
  // The types export flushSync but the runtime provides flushSyncWork instead.
  // This is a known issue with the package types, not something we can fix via version alignment.
  type ReconcilerWithSync = typeof reconcilerInstance & {
    updateContainerSync: typeof reconcilerInstance.updateContainer;
    flushSyncWork: () => void;
  };
  const reconciler = reconcilerInstance as ReconcilerWithSync;

  // Runtime check for sync API availability
  if (typeof reconciler.updateContainerSync !== 'function' || typeof reconciler.flushSyncWork !== 'function') {
    throw new Error(
      'react-reconciler sync API not available. ' +
        'Expected updateContainerSync and flushSyncWork methods. ' +
        `Found: updateContainerSync=${typeof reconciler.updateContainerSync}, ` +
        `flushSyncWork=${typeof reconciler.flushSyncWork}`,
    );
  }

  reconciler.updateContainerSync(element, container, null, () => {
    /* noop */
  });
  reconciler.flushSyncWork();

  // Return the first real child (should be the dashboard-grid)
  return root.children[0] ?? root;
}

/**
 * Checks if a JSX element name is a known component type.
 */
export function isKnownElementType(elementName: string): boolean {
  return elementName in nodeCreators;
}

/**
 * Gets all registered JSX element names.
 * Useful for debugging and error messages.
 */
export function getRegisteredElementNames(): string[] {
  return Object.keys(nodeCreators);
}
